import { useRef, useState, useCallback, type DragEvent, type KeyboardEvent } from 'react';

export interface FileDropzoneProps {
  file: File | null;
  onFileSelect: (file: File | null) => void;
  disabled?: boolean;
  prompt: string;
  accept?: string;
  validateFile?: (file: File) => { isValid: boolean; error?: string };
  onValidationError?: (error: string) => void;
}

export function FileDropzone({
  file,
  onFileSelect,
  disabled = false,
  prompt,
  accept,
  validateFile,
  onValidationError,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileValidation = useCallback((selectedFile: File) => {
    if (validateFile) {
      const validation = validateFile(selectedFile);
      if (!validation.isValid && validation.error) {
        onValidationError?.(validation.error);
        return false;
      }
    }
    return true;
  }, [validateFile, onValidationError]);

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);

      if (disabled) {
        return;
      }

      const droppedFile = event.dataTransfer.files[0];
      if (droppedFile && handleFileValidation(droppedFile)) {
        onFileSelect(droppedFile);
      }
    },
    [disabled, onFileSelect, handleFileValidation]
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile && handleFileValidation(selectedFile)) {
      onFileSelect(selectedFile);
    }
  }, [onFileSelect, handleFileValidation]);

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        onChange={handleChange}
        className="hidden"
        disabled={disabled}
        accept={accept}
      />
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={[
          'flex min-h-28 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        ].join(' ')}
      >
        <svg
          className="text-onsurface/60 size-8"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" x2="12" y1="3" y2="15" />
        </svg>
        {file ? (
          <span className="text-onsurface break-all text-sm font-medium">
            {file.name}
          </span>
        ) : (
          <span className="text-onsurface/70 text-sm">{prompt}</span>
        )}
      </div>
    </div>
  );
}
